import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, ArrowRight, XCircle } from "lucide-react";

const POLL_MS = 2000;
const MAX_POLLS = 30;

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [tier, setTier] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      polls.current += 1;
      try {
        const m = await api.get("/subscriptions/me");
        if (m.data.tier && m.data.tier !== "free") {
          setTier(m.data.tier);
          setState("paid");
          return;
        }
      } catch (_e) { /* transient — retry */ }
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center px-6" data-testid="sub-success-page">
      <div className="max-w-md w-full text-center">
        {state === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-4" />
            <h1 className="font-display uppercase text-3xl font-bold">Activating your plan…</h1>
            <p className="text-stone-400 mt-2 text-sm">Stripe is confirming the subscription. Usually a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-500 mb-4" />
            <h1 className="font-display uppercase text-3xl font-bold">
              Welcome to Rodeo {tier?.charAt(0).toUpperCase()}{tier?.slice(1)}
            </h1>
            <p className="text-stone-400 mt-2 text-sm">Your plan is live. Head to Rodeo Box and start uploading.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
              <button onClick={() => navigate("/portal/studio")} className="btn-primary inline-flex items-center gap-2 text-sm">
                Open Rodeo Box <ArrowRight size={14} />
              </button>
              <button onClick={() => navigate("/portal/upgrade")} className="btn-secondary inline-flex items-center gap-2 text-sm">
                See my plan
              </button>
            </div>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={48} className="mx-auto text-stone-500 mb-4" />
            <h1 className="font-display uppercase text-3xl font-bold">Still processing</h1>
            <p className="text-stone-400 mt-2 text-sm">Give Stripe a moment. If the plan doesn&apos;t show up in a minute, refresh Upgrade.</p>
            <button onClick={() => navigate("/portal/upgrade")} className="btn-primary mt-6 inline-flex items-center gap-2 text-sm">
              Back to Upgrade
            </button>
          </>
        )}
      </div>
    </div>
  );
}
